/**
 * Tipos de Resposta do CorelDRAW - Centralizado
 * 
 * Este arquivo centraliza TODOS os tipos de resposta que o frontend recebe
 * do CorelDRAW via WebView2, organizados por categoria de ação.
 * 
 * @module CorelResponseTypes
 * 
 * @see docs/03-integration/corel-response-types.md para documentação completa
 */

import type { WebViewResponse } from './messages';

// ============================================================================
// ESTRUTURA BASE
// ============================================================================

/**
 * Resposta genérica de sucesso (sem dados específicos)
 */
export interface GenericSuccessResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    mensagem?: string;
  };
}

/**
 * Resposta genérica de erro
 */
export interface GenericErrorResponse extends WebViewResponse {
  status: 'erro';
  message: string;
  code?: string;  // Código de erro UPPER_SNAKE_CASE
  error?: {
    details?: string;
    path?: string;
    [key: string]: any;
  };
}

// ============================================================================
// ACTIONS DE ARQUIVO
// ============================================================================

/**
 * Resposta de abrir-arquivo
 */
export interface OpenFileResponse extends WebViewResponse {
  status: 'ok' | 'erro';
  result?: {
    sucesso: boolean;
    mensagem: string;
    caminhoArquivo: string;
    nomeArquivo: string;
    validacao?: {
      realizada: boolean;
      hashOk: boolean;
      arquivoModificado: boolean;
    };
    dadosArquivo?: {
      tamanho: number;
      dataModificacao: string;
    };
    validacaoMetadados?: {
      totalObjetos: number;
      objetosComMetadados: number;
    };
    numeroObjetos: number;
    hashArquivo: string | null;  // SHA256 hex lowercase (null se > 200MB)
    tempoAbertura: number;       // ms
    informacoesDocumento?: {
      largura: number;
      altura: number;
      unidades: string;
    };
  };
}

/**
 * Resposta de salvararquivo
 */
export interface SaveFileResponse extends WebViewResponse {
  status: 'ok' | 'erro';
  result?: {
    sucesso: boolean;
    mensagem: string;
    caminhoSalvo?: string;
    integridade?: {
      hashCalculado: string;
      hashEsperado?: string;
      validacaoOk: boolean;
    };
    metadata?: {
      numeroObjetos: number;
      tempoSalvamento: number;
      tamanhoArquivo: number;
    };
  };
}

/**
 * Resposta de fechar-documento
 */
export interface CloseDocumentResponse extends WebViewResponse {
  status: 'ok' | 'erro';
  result?: {
    sucesso: boolean;
    mensagem?: string;
    acao?: string;
  };
}

// ============================================================================
// ACTIONS DE EXPORTAÇÃO
// ============================================================================

/**
 * Resposta de progresso de exportação
 */
export interface ExportProgressResponse extends WebViewResponse {
  status: 'progress_update';
  result: {
    status: 'start' | 'progress' | 'file_success' | 'file_error' | 'complete';
    current?: number;
    total?: number;
    file?: string;
    error?: string;
  };
}

/**
 * Resposta completa de exportação em lote
 */
export interface ExportBatchCompleteResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    totalExportados: number;
    totalErros: number;
    arquivos: Array<{
      caminho: string;
      sucesso: boolean;
      erro?: string;
    }>;
  };
}

/**
 * Resposta de exportação PNG
 */
export interface ExportPngResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    totalExportados: number;
    arquivos: Array<{
      staticId: string;
      caminho: string;
      sucesso: boolean;
    }>;
  };
}

// ============================================================================
// ACTIONS DE OBJETOS
// ============================================================================

/**
 * Resposta de nomear-objetos
 * 
 * @see CorelContext.tsx linha 113 - processa objetos com id/Id
 */
export interface NameObjectsResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    mensagem: string;
    total: number;
    objetos: Array<{
      id?: string | number;
      Id?: string | number;
      nome: string;
      StaticID?: string;
      Guid?: string;
      [key: string]: any;
    }>;
  };
}

/**
 * Resposta de focar-objeto-por-staticid
 */
export interface FocusObjectResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    mensagem: string;
    staticId?: string;
    encontrado: boolean;
  };
}

/**
 * Resposta de listar-camadas
 */
export interface ListLayersResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    camadas: Array<{
      nome: string;
      visivel: boolean;
      bloqueada: boolean;
      numeroObjetos: number;
    }>;
  };
}

// ============================================================================
// ACTIONS DE ORGANIZAÇÃO
// ============================================================================

/**
 * Resposta de organizar-camada
 * 
 * @see CorelContext.tsx linha 91 - processa objetos com Largura/largura
 */
export interface OrganizeLayerResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    mensagem: string;
    total: number;
    objetos: Array<{
      StaticID: string;
      Guid: string;
      Largura?: number;
      largura?: number;
      Altura?: number;
      altura?: number;
      [key: string]: any;
    }>;
  };
}

// ============================================================================
// ACTIONS DE MONITORAMENTO
// ============================================================================

/**
 * Dados de status do monitoramento
 */
export interface MonitorStatusData {
  ativo: boolean;
  objetosMonitorados: number;
  documentoAtivo: string;
  documentoDisponivel: boolean;
  docKey?: string;
}

/**
 * Dados de sync do monitoramento
 */
export interface MonitorSyncData {
  objetosMonitorados: number;
  documentoAtivo: string;
  objetos: Array<{
    StaticID: string;
    Guid: string;
    [key: string]: any;
  }>;
}

/**
 * Dados de iniciar monitoramento
 */
export interface MonitorIniciarData {
  docKey: string;
  objetosMonitorados: number;
  configuracao: any;
}

/**
 * Resposta padronizada de monitorar-objetos
 * 
 * @see MonitorResponseAdapter para processamento
 */
export interface MonitorCommandResponse<T = any> extends WebViewResponse {
  action: 'monitorar-objetos';
  status: 'ok' | 'erro';
  result: {
    comando: 'iniciar' | 'parar' | 'status' | 'sync';
    sucesso: boolean;
    mensagem: string | null;
    data: T | null;
  };
}

// ============================================================================
// ACTIONS DE SISTEMA
// ============================================================================

/**
 * Resposta de app-status
 * 
 * @see useCorelConnection.ts linha 127 - processa comunicação e sistema
 */
export interface AppStatusResponse extends WebViewResponse {
  status: 'ok' | 'erro';
  result?: {
    communication: {
      status: 'ok' | 'degraded';
      deserializationSuccessful: boolean;
      allFieldsReceived: boolean;
      roundTripTimeMs?: number;
    };
    system: {
      corelStatus: 'online' | 'offline' | 'unknown';
    };
    echo?: {
      requestReceived: string;
    };
  };
}

/**
 * Resposta de health:pong
 */
export interface HealthPongResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    nonce?: string | number;
    timestamp?: number;
  };
}

// ============================================================================
// ACTIONS DE METADADOS
// ============================================================================

/**
 * Resposta de metadata:collect
 */
export interface MetadataCollectResponse extends WebViewResponse {
  status: 'ok';
  result: {
    sucesso: boolean;
    total: number;
    objetos: Array<{
      staticId: string;
      metadata: Record<string, any>;
    }>;
  };
}

// ============================================================================
// ACTIONS DE VBCOMPILER
// ============================================================================

/**
 * Resposta de progresso do VBCompiler
 */
export interface VBCompilerProgressResponse extends WebViewResponse {
  status: 'progress_update';
  result?: {
    current?: number;
    total?: number;
    percent?: number;
    message?: string;
  };
  data?: {
    current?: number;
    total?: number;
    percent?: number;
    message?: string;
  };
}

/**
 * Resposta final do VBCompiler
 */
export interface VBCompilerCompleteResponse extends WebViewResponse {
  status: 'ok' | 'error' | 'cancelled';
  result?: any;
  data?: any;
  error?: string;
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Verifica se é resposta de organização de camadas
 */
export function isOrganizeLayerResponse(response: WebViewResponse): response is OrganizeLayerResponse {
  return (
    response.status === 'ok' &&
    response.result?.objetos &&
    Array.isArray(response.result.objetos) &&
    response.result.objetos.length > 0 &&
    ('Largura' in response.result.objetos[0] || 'largura' in response.result.objetos[0])
  );
}

/**
 * Verifica se é resposta de nomeação de objetos
 */
export function isNameObjectsResponse(response: WebViewResponse): response is NameObjectsResponse {
  return (
    response.status === 'ok' &&
    response.result?.objetos &&
    Array.isArray(response.result.objetos) &&
    response.result.objetos.length > 0 &&
    ('id' in response.result.objetos[0] || 'Id' in response.result.objetos[0])
  );
}

/**
 * Verifica se é resposta de abrir arquivo
 */
export function isOpenFileResponse(response: WebViewResponse): response is OpenFileResponse {
  return response.status === 'ok' && response.result?.sucesso !== undefined;
}

/**
 * Verifica se é resposta genérica de sucesso (sem dados específicos)
 */
export function isGenericSuccessResponse(response: WebViewResponse): response is GenericSuccessResponse {
  return (
    response.status === 'ok' &&
    response.result &&
    typeof response.result === 'object' &&
    'sucesso' in response.result &&
    Object.keys(response.result).length <= 2  // sucesso + mensagem opcional
  );
}

/**
 * Verifica se é resposta de monitoramento
 */
export function isMonitorCommandResponse(response: WebViewResponse): response is MonitorCommandResponse {
  return (
    (response as any).action === 'monitorar-objetos' &&
    response.result?.comando !== undefined
  );
}

/**
 * Verifica se é resposta de app-status
 */
export function isAppStatusResponse(response: WebViewResponse): response is AppStatusResponse {
  return (
    response.status === 'ok' &&
    response.result?.communication !== undefined &&
    response.result?.system !== undefined
  );
}

// ============================================================================
// UNION TYPES
// ============================================================================

/**
 * União de todos os tipos de resposta conhecidos
 */
export type KnownResponseType =
  | OpenFileResponse
  | SaveFileResponse
  | CloseDocumentResponse
  | ExportProgressResponse
  | ExportBatchCompleteResponse
  | ExportPngResponse
  | NameObjectsResponse
  | FocusObjectResponse
  | ListLayersResponse
  | OrganizeLayerResponse
  | MonitorCommandResponse
  | AppStatusResponse
  | HealthPongResponse
  | MetadataCollectResponse
  | VBCompilerProgressResponse
  | VBCompilerCompleteResponse
  | GenericSuccessResponse
  | GenericErrorResponse;

